import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocationProvider } from './context/LocationContext';
import { ReviewProvider } from './context/ReviewContext';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Locations from './pages/Locations';
import Reviews from './pages/Reviews';
import Subscription from './pages/Subscription';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import { ToastContainer } from './components/common/Toast';
import { TOAST_TYPES } from './utils/constants';
import { generateId } from './utils/helpers';
import LoadingSpinner from './components/common/LoadingSpinner';

function App() {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = TOAST_TYPES.INFO) => {
    const id = generateId();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  window.showToast = addToast;

  return (
    <BrowserRouter>
      <AuthProvider>
        <LocationProvider>
          <ReviewProvider>
            <ToastContainer toasts={toasts} removeToast={removeToast} />
            
            <Routes>
              <Route path="/auth/callback" element={<GoogleCallback />} />
              
              {/* Public pages */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<Contact />} />
              
              <Route path="/" element={<Layout />}>
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="locations" element={<Locations />} />
                <Route path="reviews" element={<Reviews />} />
                <Route path="subscription" element={<Subscription />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="settings" element={<Settings />} />
              </Route>
              
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ReviewProvider>
        </LocationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

const GoogleCallback = () => {
  const { handleGoogleCallback } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      const error = searchParams.get('error');

      if (error) {
        window.showToast('Login failed: ' + error, TOAST_TYPES.ERROR);
        navigate('/');
        setLoading(false);
        return;
      }

      const result = await handleGoogleCallback(searchParams);

      if (result.success) {
        window.showToast('Successfully connected to Google!', TOAST_TYPES.SUCCESS);
        navigate('/dashboard');
      } else {
        window.showToast(result.error || 'Authentication failed', TOAST_TYPES.ERROR);
        navigate('/');
      }

      setLoading(false);
    };

    processCallback();
  }, [handleGoogleCallback, navigate, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F9FC' }}>
        <LoadingSpinner text="Connecting to Google..." />
      </div>
    );
  }

  return null;
};

export default App;
